<script lang="ts">
  import type { CarouselRootProps } from '../carousel.types';

  import { onMount, setContext } from 'svelte';
  import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel';
  import { CaretLeft } from 'phosphor-svelte';

  import { css } from '@agapi/ui/panda/css';
  import { Button } from '../../button';

  import {
    carouselButtonStyles,
    carouselFadeStyles,
    carouselStyles,
    dotStyles,
    indicatorStyles
  } from '../carousel.styles';

  const context = setContext('carousel', {
    count: 0,
    reInit: () => {
      if (api) {
        api.reInit();

        ({ pages, canScrollPrev, canScrollNext, activeIndex, center } = {
          pages: api.scrollSnapList().length,
          canScrollPrev: api.canScrollPrev(),
          canScrollNext: api.canScrollNext(),
          activeIndex: api.selectedScrollSnap(),
          center: !api.canScrollNext() && !api.canScrollPrev() && centerOnOverflow
        });
      } else {
        console.warn('🎠 Manual reInit called but no API available');
      }
    }
  });

  const {
    children,
    hideNavigation,
    fadeEdges,
    hideIndicators,
    centerOnOverflow = false,
    options = { align: 'start' },
    rootStyles,
    wrapperStyles,
    css: cssProp
  }: CarouselRootProps = $props();

  let rootNode = $state<HTMLElement>();
  let api: EmblaCarouselType | undefined = $state();

  let canScrollPrev = $state(false);
  let canScrollNext = $state(true);
  let center = $state(false);
  let pages = $state(Math.ceil(context.count / (typeof options.slidesToScroll === 'number' ? options.slidesToScroll : 1))); // Defaults, will be overwritten
  let activeIndex = $state(0);

  onMount(() => {
    if (rootNode) {
      api = EmblaCarousel(rootNode, options);

      // Set initial state
      const initialPages = api.scrollSnapList().length;
      const initialCanScrollPrev = api.canScrollPrev();
      const initialCanScrollNext = api.canScrollNext();
      const initialActiveIndex = api.selectedScrollSnap();
      const initialCenter = !api.canScrollNext() && !api.canScrollPrev() && centerOnOverflow;

      pages = initialPages;
      canScrollPrev = initialCanScrollPrev;
      canScrollNext = initialCanScrollNext;
      activeIndex = initialActiveIndex;
      center = initialCenter;

      api.on('select', (api) => {
        canScrollPrev = api.canScrollPrev();
        canScrollNext = api.canScrollNext();
        activeIndex = api.selectedScrollSnap();
        pages = api.scrollSnapList().length;
      });

      api.on('resize', (api) => {
        pages = api.scrollSnapList().length;
        canScrollPrev = api.canScrollPrev();
        canScrollNext = api.canScrollNext();
        center = !api.canScrollNext() && !api.canScrollPrev() && centerOnOverflow;
      });

      // More robust content change detection
      let mutationTimeout: ReturnType<typeof setTimeout> | null = null;

      const handleContentChange = () => {
        if (mutationTimeout) clearTimeout(mutationTimeout);

        mutationTimeout = setTimeout(() => {
          if (api) {
            api.reInit();

            pages = api.scrollSnapList().length;
            canScrollPrev = api.canScrollPrev();
            canScrollNext = api.canScrollNext();
            activeIndex = api.selectedScrollSnap();
            center = !api.canScrollNext() && !api.canScrollPrev() && centerOnOverflow;
          }
        }, 100); // Longer debounce to handle multiple rapid changes
      };

      // Watch for changes in the carousel container
      const container = rootNode.querySelector('[data-carousel-container]');
      const mutationObserver = new MutationObserver((mutations) => {
        let shouldReinit = false;

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            shouldReinit = true;
          }
        });

        if (shouldReinit) {
          handleContentChange();
        }
      });

      if (container) {
        mutationObserver.observe(container, {
          childList: true,
          subtree: false // Only watch direct children
        });
      } else {
        console.warn('🎠 Container not found for MutationObserver');
      }

      // Cleanup function
      return () => {
        if (mutationTimeout) clearTimeout(mutationTimeout);
        mutationObserver.disconnect();
        if (api) {
          api.destroy();
        }
      };
    } else {
      console.warn('🎠 No rootNode found during onMount');
    }
  });

  const scrollPrev = () => {
    if (api) {
      api.scrollPrev();
      const newCanScrollPrev = api.canScrollPrev();
      const newCanScrollNext = api.canScrollNext();
      canScrollPrev = newCanScrollPrev;
      canScrollNext = newCanScrollNext;
    } else {
      console.warn('🎠 scrollPrev called but no API available');
    }
  };

  const scrollNext = () => {
    if (api) {
      api.scrollNext();
      const newCanScrollPrev = api.canScrollPrev();
      const newCanScrollNext = api.canScrollNext();
      canScrollPrev = newCanScrollPrev;
      canScrollNext = newCanScrollNext;
    } else {
      console.warn('🎠 scrollNext called but no API available');
    }
  };

  let leftMask = $derived(fadeEdges && canScrollPrev ? 33 : 0);
  let rightMask = $derived(fadeEdges && canScrollNext ? 33 : 0);

  const classes = $derived(carouselStyles.raw({ center }));
</script>

<div class={css(classes.root, rootStyles)}>
  <div
    bind:this={rootNode}
    style="--leftMask: {leftMask}px; --rightMask: {rightMask}px"
    class={css(classes.wrapper, carouselFadeStyles, wrapperStyles)}
  >
    <div class={css(classes.container, cssProp)} data-carousel-container>
      {#if children}
        {@render children()}
      {/if}
    </div>
  </div>

  {#if !hideIndicators}
    <div class={indicatorStyles}>
      {#each Array.from({ length: pages }) as _index, idx}
        <div class={dotStyles({ active: idx === activeIndex })}></div>
      {/each}
    </div>
  {/if}

  {#if !hideNavigation}
    <Button
      size="xs"
      variant="secondary"
      onclick={scrollPrev}
      css={carouselButtonStyles.raw({ direction: 'prev', visible: canScrollPrev })}
      aria-label="Previous"
    >
      <CaretLeft width={21} />
    </Button>

    <Button
      size="xs"
      variant="secondary"
      onclick={scrollNext}
      css={carouselButtonStyles.raw({ direction: 'next', visible: canScrollNext })}
      aria-label="Next"
    >
      <CaretLeft width={21} />
    </Button>
  {/if}
</div>

<style>
  /* Define mask properties to allow for animating gradients */
  @property --leftMask {
    syntax: '<length>';
    inherits: true;
    initial-value: 33px;
  }
  @property --rightMask {
    syntax: '<length>';
    inherits: true;
    initial-value: 33px;
  }
</style>
